import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './session';
import { AppShell } from './layouts';

const Landing = React.lazy(() => import('./pages/Landing'));
const Register = React.lazy(() => import('./pages/Register'));
const OverviewPage = React.lazy(() => import('./pages/OverviewPage'));
const CallLogsPage = React.lazy(() => import('./pages/CallLogsPage'));
const CompleteSignup = React.lazy(() => import('./pages/CompleteSignup'));
const ManageAgents = React.lazy(() => import('./pages/ManageAgents'));
const OpsPage = React.lazy(() => import('./pages/OpsPage'));
const OpsAccountDataPage = React.lazy(() => import('./pages/OpsAccountDataPage'));

function LoadingScreen({ message = 'Loading…' }: { message?: string }) {
  return (
    <div className="min-h-screen bg-surfaceAlt flex items-center justify-center px-4">
      <div
        className="tv-card w-full max-w-xs flex flex-col items-center gap-3 text-center py-6"
        role="status"
        aria-live="polite"
      >
        <div className="tv-spinner" aria-hidden />
        <p className="text-[15px] font-semibold text-ink">{message}</p>
      </div>
    </div>
  );
}

function RootPage() {
  const session = useSession();

  if (session.status === 'checking') return <LoadingScreen message="Verifying login…" />;
  if (session.status === 'unauth') return <Landing />;

  if (session.isActive) {
    return session.hasPhone && session.hasBilling
      ? <Navigate to="/app" replace />
      : <Navigate to="/register" replace />;
  }
  return <Navigate to="/completeSignup" replace />;
}

function RequireOnboardingLayout() {
  const session = useSession();
  const location = useLocation();

  if (session.status === 'checking') return <LoadingScreen message="Loading…" />;
  if (session.status === 'unauth') return <Navigate to="/" replace />;
  if (session.isActive && session.hasPhone && session.hasBilling) return <Navigate to="/app" replace />;

  if (session.isActive && (!session.hasPhone || !session.hasBilling)) {
    if (location.pathname === '/completeSignup') return <Navigate to="/register" replace />;
    return <AppShell showNav={false} />;
  }

  if (!session.isActive) {
    if (location.pathname === '/register') return <Navigate to="/completeSignup" replace />;
    return <AppShell showNav={false} />;
  }

  return <AppShell showNav={false} />;
}

function RequireRegisteredLayout() {
  const session = useSession();

  if (session.status === 'checking') return <LoadingScreen message="Loading…" />;
  if (session.status === 'unauth') return <Navigate to="/" replace />;
  if (!session.isActive) return <Navigate to="/completeSignup" replace />;
  if (!session.hasPhone || !session.hasBilling) return <Navigate to="/register" replace />;

  return <AppShell />;
}

export default function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <React.Suspense fallback={<LoadingScreen message="Loading app…" />}>
          <Routes>
            <Route path="/" element={<RootPage />} />
            <Route element={<RequireOnboardingLayout />}>
              <Route path="/completeSignup" element={<CompleteSignup />} />
              <Route path="/register" element={<Register />} />
            </Route>
            <Route element={<RequireRegisteredLayout />}>
              <Route path="/app" element={<OverviewPage />} />
              <Route path="/calls" element={<CallLogsPage />} />
              <Route path="/agents" element={<ManageAgents />} />
              <Route path="/ops" element={<OpsPage />} />
              <Route path="/ops/account/:accountId" element={<OpsAccountDataPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </SessionProvider>
  );
}
